import Big from 'big.js';
import {BehaviorSubject, Subject, combineLatest} from 'rxjs';
import {switchMap, skipUntil, map} from 'rxjs/operators';
import {timeUtil} from '@/helper/time';
import {calcCurrentQty} from '@/helper/contract';
import {gt, numUtil, plus} from '@/helper/num';
import {mc} from '@/core';
import {assetsApi} from '@/api/assets';
import {browser$} from '@/streams/browser';
import {contractsCache$} from '@/streams/config/contract';
import {currencies$} from '@/streams/config/currency';
import {qtyTypeBeforeV2$, qtyTypeV2$} from '@/streams/contract';

export const loading$ = new Subject();

export const closedQuery$ = new Subject();
const closedPositionData$ = new BehaviorSubject({total: 0, rows: []});
export const closedPositionList$ = combineLatest([closedPositionData$, contractsCache$, currencies$, qtyTypeBeforeV2$, qtyTypeV2$]).pipe(map(convertClosePosition));

closedQuery$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadClosedPositionList(query))
  )
  .subscribe();

async function loadClosedPositionList({currency, offset, symbol, isPerpetualV2}) {
  loading$.next(true);
  const query = {
    currency,
    offset,
    symbol,
    limit: 10,
    withCount: true,
  };
  const closedPositionList = isPerpetualV2 ? assetsApi.closedPositionListV2 : assetsApi.closedPositionList;
  const {data, error} = await closedPositionList(query);
  loading$.next(false);
  if (data) {
    closedPositionData$.next(data);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

function baseConvertClosePosition([data, contractsMap, currencies], isV2Api) {
  const qtyTypeBeforeV2 = qtyTypeBeforeV2$.getValue();
  const qtyTypeV2 = qtyTypeV2$.getValue();

  const {rows, total} = data;
  const list = rows
    .map((item) => {
      const contract = contractsMap.get(item.symbol);
      if (!contract) {
        return {
          createdTime: '-',
          symbol: item.symbol + '--Margin',
          openTime: '-',
          closeTime: '-',
          openPrice: '-',
          closePrice: '-',
          totalSize: '-',
          closedPnl: '-',
          tradingFeePaid: '-',
          tradingPtFeePaid: '-',
          fundingFeePaid: '-',
          realisedPnl: '-',
          roi: '-',
        };
      }
      const {isPerpetualV2, pricePrecision, priceScale, settleCurrencyDisplay: settleCurrency, symbolV2} = contract;
      const currency = currencies.find((o) => o.currency === contract.settleCurrency);
      if (!currency) {
        return {
          createdTime: timeUtil.formatDateTime(item.createdAt),
          symbol: `${symbolV2} ${settleCurrency}-Margin`,
          openTime: '-',
          closeTime: '-',
          openPrice: '-',
          closePrice: '-',
          totalSize: '-',
          closedPnl: '-',
          tradingFeePaid: '-',
          tradingPtFeePaid: '-',
          fundingFeePaid: '-',
          realisedPnl: '-',
          roi: '-',
        };
      }
      const {valueScale, valuePrecision} = currency;
      const {openedTimeNs, updatedTimeNs} = item;
      const data = {
        createdTime: timeUtil.formatDateTime(item.createdAt),
        symbol: `${symbolV2} ${settleCurrency}-Margin`,
        openTime: timeUtil.formatUTCDateTime(openedTimeNs),
        closeTime: timeUtil.formatUTCDateTime(updatedTimeNs),
      };

      if (isPerpetualV2 || isV2Api) {
        const {openPrice, closePrice, side, closedSizeRq, closedPnlRv, exchangeFeeRv, cumPtFeeRv, fundingFeeRv, realizedPnlRv, roi, termPtFeeRv} = item;
        const qtyPrice = Number(closePrice) !== 0 ? closePrice : openPrice;
        return Object.assign(data, {
          openPrice: openPrice || '-',
          closePrice: closePrice || '-',
          totalSize: (side === 1 ? '' : '-') + calcCurrentQty(contract, closedSizeRq, qtyPrice, qtyTypeBeforeV2, qtyTypeV2),
          closedPnl: closedPnlRv + ' ' + settleCurrency,
          tradingFeePaid: exchangeFeeRv + ' ' + settleCurrency,
          tradingPtFeePaid: termPtFeeRv + ' ' + 'PT',
          fundingFeePaid: fundingFeeRv + ' ' + settleCurrency,
          realisedPnl: realizedPnlRv + ' ' + settleCurrency,
          roi: closePrice && roi ? numUtil.floor(roi, 2, -2) + ' ' + '%' : '-%',
        });
      }

      const {openPriceEp, closePriceEp, side, closedSize, closedPnlEv, exchangeFeeEv, fundingFeeEv, realizedPnlEv, roiEr} = item;
      const openPrice = openPriceEp ? numUtil.floor(openPriceEp, pricePrecision, priceScale) : '-';
      const closePrice = closePriceEp ? numUtil.floor(closePriceEp, pricePrecision, priceScale) : '-';
      const qtyPrice = Number(closePriceEp) !== 0 ? closePrice : openPrice;
      return Object.assign(data, {
        openPrice,
        closePrice,
        totalSize: (side === 1 ? '' : '-') + calcCurrentQty(contract, closedSize, qtyPrice, qtyTypeBeforeV2, qtyTypeV2),
        closedPnl: numUtil.floor(closedPnlEv, valuePrecision, valueScale) + ' ' + settleCurrency,
        tradingFeePaid: numUtil.floor(exchangeFeeEv, valuePrecision, valueScale) + ' ' + settleCurrency,
        tradingPtFeePaid: '-',
        fundingFeePaid: numUtil.floor(fundingFeeEv, valuePrecision, valueScale) + ' ' + settleCurrency,
        realisedPnl: numUtil.floor(realizedPnlEv, valuePrecision, valueScale) + ' ' + settleCurrency,
        roi: closePriceEp ? numUtil.floor(Big(roiEr).div(1e6), 2) + '%' : '-%',
      });
    })
    .filter(Boolean);
  return {rows: list, total};
}

function convertClosePosition([data, contractsMap, currencies]) {
  return baseConvertClosePosition([data, contractsMap, currencies]);
}

export function convertClosePositionExport([rows, contractsMap, currencies]) {
  return baseConvertClosePosition([{rows}, contractsMap, currencies], true);
}

export const exchangeFeeQuery$ = new Subject();
const exchangeFeeData$ = new BehaviorSubject({total: 0, rows: []});
export const exchangeFeeList$ = combineLatest([exchangeFeeData$, contractsCache$, currencies$]).pipe(map(convertExchangeFee));

exchangeFeeQuery$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadExchangeFeeList(query))
  )
  .subscribe();

async function loadExchangeFeeList({currency, offset, symbol, isPerpetualV2}) {
  loading$.next(true);
  const query = {
    currency,
    offset,
    symbol,
    limit: 10,
    withCount: true,
  };
  const {data, error} = isPerpetualV2 ? await assetsApi.exchangeFeeListV2(query) : await assetsApi.exchangeFeeList(query);
  loading$.next(false);
  if (data) {
    exchangeFeeData$.next(data);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

function convertExchangeFee([data, contractsCache, currencies]) {
  const {rows, total} = data;
  const list = rows.map((item) => {
    const contract = contractsCache.get(item.symbol);
    if (!contract) {
      return {
        createdTime: '-',
        symbol: item.symbol + '--Margin',
        takerVolume: '-',
        makerVolume: '-',
        takeFeeRate: '-',
        makerFeeRate: '-',
        tradingFeePaid: '-',
        tradingPtFeePaid: '-',
      };
    }
    const {isPerpetualV2, settleCurrencyDisplay: settleCurrency, symbolV2} = contract;
    const currency = currencies.find((o) => o.currency === contract.settleCurrency);
    if (!currency) {
      return {
        createdTime: '-',
        symbol: item.symbol + '--Margin',
        takerVolume: '-',
        makerVolume: '-',
        takeFeeRate: '-',
        makerFeeRate: '-',
        tradingFeePaid: '-',
        tradingPtFeePaid: '-',
      };
    }
    const {valueScale, valuePrecision} = currency;
    const data = {
      createdTime: timeUtil.formatDateTime(item.createdAt),
      symbol: `${symbolV2} ${settleCurrency}-Margin`,
    };
    if (isPerpetualV2) {
      const {takerValueRv, makerValueRv, takerRateRr, makerRateRr, exchangeFeeValueRv, ptMakerFeeRv, ptTakerFeeRv} = item;
      const tradingPtFeePaid = plus(ptMakerFeeRv || 0, ptTakerFeeRv || 0);
      Object.assign(data, {
        takerVolume: (Number(takerValueRv) ? takerValueRv : '-') + ' ' + settleCurrency,
        makerVolume: (Number(makerValueRv) ? makerValueRv : '-') + ' ' + settleCurrency,
        takeFeeRate: (Number(takerRateRr) ? numUtil.floor(takerRateRr, 4, -2) : '-') + '%',
        makerFeeRate: (Number(makerRateRr) ? numUtil.floor(makerRateRr, 4, -2) : '-') + '%',
        tradingFeePaid: exchangeFeeValueRv + ' ' + settleCurrency,
        tradingPtFeePaid: (gt(tradingPtFeePaid, 0) ? tradingPtFeePaid : '-') + ' ' + 'PT',
      });
      return data;
    }
    const {takerValueEv, makerValueEv, takerRateEr, makerRateEr, exchangeFeeValueEv} = item;
    Object.assign(data, {
      takerVolume: (takerValueEv ? numUtil.floor(takerValueEv, valuePrecision, valueScale) : '-') + ' ' + settleCurrency,
      makerVolume: (makerValueEv ? numUtil.floor(makerValueEv, valuePrecision, valueScale) : '-') + ' ' + settleCurrency,
      takeFeeRate: (takerRateEr ? numUtil.floor(takerRateEr, 4, 6) : '-') + '%',
      makerFeeRate: (makerRateEr ? numUtil.floor(makerRateEr, 4, 6) : '-') + '%',
      tradingFeePaid: numUtil.floor(exchangeFeeValueEv, valuePrecision, valueScale) + ' ' + settleCurrency,
      tradingPtFeePaid: '-',
    });
    return data;
  });
  return {rows: list, total};
}

export const fundingFeeQuery$ = new Subject();
fundingFeeQuery$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadFundingFeeList(query))
  )
  .subscribe();

const fundingData$ = new BehaviorSubject({total: 0, rows: []});
export const fundingFeeList$ = combineLatest([fundingData$, contractsCache$, currencies$, qtyTypeBeforeV2$, qtyTypeV2$]).pipe(map(convertFundingFee));

async function loadFundingFeeList({currency, offset, symbol, isPerpetualV2}) {
  loading$.next(true);
  const query = {
    currency,
    symbol,
    offset,
    limit: 10,
    withCount: true,
  };
  const {data, error} = isPerpetualV2 ? await assetsApi.fundingFeeListV2(query) : await assetsApi.fundingFeeList(query);
  loading$.next(false);
  if (data) {
    fundingData$.next(data);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

export const queryPnl$ = new Subject();
const pnlHistoryData$ = new BehaviorSubject([]);
export const pnlHistoryItems$ = new BehaviorSubject([]);

queryPnl$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadPnlData(query))
  )
  .subscribe((x) => pnlHistoryItems$.next(x));

async function loadPnlData({days, symbol, currency, isPerpetualV2}) {
  loading$.next(true);
  const query = {
    days,
    symbol,
    currency,
  };
  if (symbol) {
    const pnlPosition = isPerpetualV2 ? assetsApi.pnlPositionV2 : assetsApi.pnlPosition;
    const {data, error} = await pnlPosition(query);
    loading$.next(false);
    if (data) {
      pnlHistoryData$.next(data);
      return data.rows;
    }
    if (error) {
      mc.send('toast', 'error', error);
    }
    return [];
  }
  const pnlAccount = isPerpetualV2 ? assetsApi.pnlAccountV2 : assetsApi.pnlAccount;
  const {data, error} = await pnlAccount(query);
  loading$.next(false);
  if (data) {
    return data.rows;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return [];
}

function convertFundingFee([data, contractsMap, currencies, qtyTypeBeforeV2, qtyTypeV2]) {
  const {rows, total} = data;
  const list = rows.map((item) => {
    const contract = contractsMap.get(item.symbol);
    if (!contract) {
      return {
        createdTime: '-',
        symbol: item.symbol + '--Margin',
        execQty: '-',
        markPrice: '-',
        markValue: '-',
        fundingRate: '-',
        execFundingRate: '-',
        fundingFeePaid: '-',
      };
    }
    const {isPerpetualV2, pricePrecision, priceScale, settleCurrencyDisplay: settleCurrency, symbolV2} = contract;
    const currency = currencies.find((o) => o.currency === contract.settleCurrency);
    if (!currency) {
      return {
        createdTime: '-',
        symbol: item.symbol + '--Margin',
        execQty: '-',
        markPrice: '-',
        markValue: '-',
        fundingRate: '-',
        execFundingRate: '-',
        fundingFeePaid: '-',
      };
    }
    const {valueScale, valuePrecision} = currency;
    const data = {
      createdTime: timeUtil.formatDateTime(item.createdAt),
      symbol: `${symbolV2} ${settleCurrency}-Margin`,
    };
    if (isPerpetualV2) {
      Object.assign(data, {
        execQty: (item.side === 1 ? '' : '-') + calcCurrentQty(contract, item.execQtyRq, item.execPriceRp, qtyTypeBeforeV2, qtyTypeV2),
        markPrice: item.execPriceRp,
        markValue: item.execValueRv + ' ' + settleCurrency,
        fundingRate: numUtil.floor(item.fundingRateRr, 4, -2) + '%',
        execFundingRate: numUtil.floor(item.feeRateRr, 4, -2) + '%',
        fundingFeePaid: item.execFeeRv + ' ' + settleCurrency,
      });
      return data;
    }
    const {execFeeEv} = item;
    const markPrice = numUtil.floor(item.execPriceEp, pricePrecision, priceScale);
    Object.assign(data, {
      execQty: (item.side === 1 ? '' : '-') + calcCurrentQty(contract, item.execQty, markPrice, qtyTypeBeforeV2, qtyTypeV2),
      markPrice,
      markValue: numUtil.floor(item.execValueEv, valuePrecision, valueScale) + ' ' + settleCurrency,
      fundingRate: numUtil.floor(item.fundingRateEr, 4, 6) + '%',
      execFundingRate: numUtil.floor(item.feeRateEr, 4, 6) + '%',
      fundingFeePaid: (execFeeEv > 0 ? numUtil.floor(Math.abs(execFeeEv), valuePrecision, valueScale) : '-' + numUtil.floor(Math.abs(execFeeEv), valuePrecision, valueScale)) + ' ' + settleCurrency,
    });
    return data;
  });
  return {rows: list, total};
}
